import React, { type FC } from 'react';
import { DataTable, type DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ReactComponent as CopyIcon } from '../../assets/ic_copy.svg';
import DateUtils from '../../utils/DateTimeUtils';
import { noop } from '../../utils/GeneralUtils';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type paths } from '../../endpoint/Schema';
import { useTestUsers } from '../../contexts/TestUsersContext';
import { useNotCustomerUsers } from '../../contexts/NotCustomerUsersContext';
import AppSetupCheck from '../../components/AppSetupCheck';

const ROWS = 10;

type DeviceStatusSummaryData =
  paths['/api/v2/admin/device-status/summary']['get']['responses']['200']['content']['application/json'][0];

const DeviceStatusSummary: FC = () => {
  const tenantId = useTenantStore(selectTenantId);
  const { includeTestUsers } = useTestUsers();
  const { includeNotCustomerUsers } = useNotCustomerUsers();
  const [lazyParams, setLazyParams] = React.useState<
    Pick<
      DataTableStateEvent,
      'first' | 'rows' | 'page' | 'sortField' | 'sortOrder'
    >
  >({
    first: 0,
    rows: ROWS,
    page: 1,
    sortField: 'lastHeartbeatTime',
    sortOrder: -1,
  });
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<DeviceStatusSummaryData[]>([]);
  const toast = React.useRef<Toast>(null);

  React.useEffect(() => {
    const getDeviceStatusSummary = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/device-status/summary', {
          params: {
            query: {
              tenantId,
              includeTestUsers,
              includeNotCustomerUsers,
            },
          },
        });

        if (res.data) setData(res.data);
      } catch (error) {
        console.error('Failed to fetch device status summary:', error);
      }

      setLoading(false);
    };

    getDeviceStatusSummary().catch(noop);
  }, [tenantId, includeTestUsers, includeNotCustomerUsers]);

  function handlePageChange(event: DataTableStateEvent) {
    setLazyParams((prev) => ({ ...prev, ...event }));
  }

  function userIdBodyTemplate(rowData: DeviceStatusSummaryData) {
    function copyId() {
      navigator.clipboard
        .writeText(rowData.userId ?? '')
        .then(() => {
          toast.current?.show({
            severity: 'success',
            summary: 'ID copied',
            life: 3000,
          });
        })
        .catch((err) => {
          toast.current?.show({
            severity: 'error',
            summary: 'Error occurred',
            detail: err instanceof Error ? err.message : '',
            life: 3000,
          });
        });
    }

    return (
      <div className="flex items-center">
        <span className="mr-2">{rowData.userId?.slice(0, 8) ?? 'N/A'}</span>
        <button onClick={copyId}>
          <CopyIcon className="w-4 h-4 fill-current text-gray-600 hover:text-gray-800" />
        </button>
      </div>
    );
  }

  function userNameBodyTemplate(rowData: DeviceStatusSummaryData) {
    return rowData.userName ?? 'N/A';
  }

  function emailBodyTemplate(rowData: DeviceStatusSummaryData) {
    return rowData.email ?? 'N/A';
  }

  function lastOkStatusBodyTemplate(rowData: DeviceStatusSummaryData) {
    if (!rowData.lastOkStatusAt) {
      return 'Never';
    }

    const formattedDate = DateUtils.formatDate(rowData.lastOkStatusAt);
    const daysSince = Math.floor(
      (new Date().getTime() - new Date(rowData.lastOkStatusAt).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    return (
      <div>
        <div>{formattedDate}</div>
        <div className="text-sm" style={{ color: '#9e9eab' }}>
          {daysSince === 0
            ? 'Today'
            : `${daysSince} day${daysSince === 1 ? '' : 's'} ago`}
        </div>
      </div>
    );
  }

  function lastUserNotificationBodyTemplate(rowData: DeviceStatusSummaryData) {
    return rowData.lastUserNotificationAt
      ? DateUtils.formatDate(rowData.lastUserNotificationAt)
      : 'Never';
  }

  function lastTripBodyTemplate(rowData: DeviceStatusSummaryData) {
    if (!rowData.lastTripEndAt) {
      return 'Never';
    }

    const formattedDate = DateUtils.formatDate(rowData.lastTripEndAt);
    const daysSince = Math.floor(
      (new Date().getTime() - new Date(rowData.lastTripEndAt).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    return (
      <div>
        <div>{formattedDate}</div>
        <div className="text-sm" style={{ color: '#9e9eab' }}>
          {daysSince === 0
            ? 'Today'
            : `${daysSince} day${daysSince === 1 ? '' : 's'} ago`}
        </div>
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Device Status Summary
        </h1>
      </div>

      <DataTable
        value={data}
        dataKey="userId"
        scrollable
        paginator
        rows={ROWS}
        first={lazyParams.first}
        totalRecords={data.length}
        loading={loading}
        rowHover
        lazy={false}
        onPage={handlePageChange}
        onSort={handlePageChange}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        emptyMessage="No device status data available."
      >
        <Column
          field="userId"
          header="User ID"
          body={userIdBodyTemplate}
          style={{ minWidth: '150px' }}
        />
        <Column
          field="userName"
          header="User Name"
          body={userNameBodyTemplate}
          sortable
        />
        <Column field="email" header="Email" body={emailBodyTemplate} />
        <Column
          field="lastOkStatusAt"
          header="Last OK Status"
          body={lastOkStatusBodyTemplate}
          sortable
        />
        <Column
          field="lastTripEndAt"
          header="Last Trip"
          body={lastTripBodyTemplate}
        />
        <Column
          field="lastUserNotificationAt"
          header="Last SMS/Push Notification"
          body={lastUserNotificationBodyTemplate}
          sortable
        />
      </DataTable>

      <AppSetupCheck pageTitle="Device Status Summary" />
      <Toast ref={toast} />
    </div>
  );
};
export default DeviceStatusSummary;
